import { TriangleAlertIcon } from "lucide-react";
import React from "react";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";
import { useIntl } from "react-intl";
import { cn } from "~/lib/utils";
import logger from "~/utils/logger";

interface ErrorBoundaryWrapperProps {
  children: React.ReactNode;
  /**
   * Full-page fallback (default). Set to `false` for boundaries nested around
   * a feature region so the fallback fills its container instead of the
   * viewport and recovery re-renders the region instead of reloading the page.
   */
  fullScreen?: boolean;
  /**
   * Values that reset the boundary when any of them change (e.g. the current
   * route). Lets a recovered region re-render its children when the user
   * navigates away instead of leaving the stale fallback in place.
   */
  resetKeys?: unknown[];
}

function ErrorFallback({ fullScreen }: { fullScreen: boolean }) {
  const { formatMessage } = useIntl();
  const { resetBoundary } = useErrorBoundary();

  return (
    <div
      className={cn(
        "flex items-center justify-center bg-muted",
        fullScreen
          ? "min-h-screen"
          : "h-full w-full min-h-[240px] rounded-lg p-4"
      )}
    >
      <div className="max-w-md w-full bg-card shadow-lg rounded-lg p-8 text-center">
        <TriangleAlertIcon className="mx-auto h-12 w-12 text-destructive" />
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          {formatMessage({ id: "errorBoundary.title" })}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {formatMessage({ id: "errorBoundary.description" })}
        </p>
        <button
          onClick={resetBoundary}
          className="mt-6 px-4 py-2 bg-brand text-brand-foreground rounded hover:bg-brand/90 transition duration-300 ease-in-out"
        >
          {formatMessage({ id: "errorBoundary.tryAgain" })}
        </button>
      </div>
    </div>
  );
}

const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({
  children,
  fullScreen = true,
  resetKeys,
}) => (
  <ErrorBoundary
    fallbackRender={() => <ErrorFallback fullScreen={fullScreen} />}
    onError={(error) =>
      logger.error("Uncaught render error in ErrorBoundaryWrapper:", error)
    }
    onReset={fullScreen ? () => window.location.reload() : undefined}
    resetKeys={resetKeys}
  >
    {children}
  </ErrorBoundary>
);

export default ErrorBoundaryWrapper;
